/**
 * Motion Manager
 * Manages reduced motion preferences for accessibility
 * Respects prefers-reduced-motion system preference
 */

import Phaser from 'phaser';

export interface AnimationConfig {
  targets: unknown;
  duration?: number;
  ease?: string;
  delay?: number;
  repeat?: number;
  yoyo?: boolean;
  alpha?: number;
  scale?: number;
  x?: number;
  y?: number;
  rotation?: number;
  [key: string]: unknown;
}

export class MotionManager {
  private reduceMotion = false;
  private listeners: Set<(reduced: boolean) => void> = new Set();

  constructor() {
    this.detectSystemPreference();
  }

  private detectSystemPreference(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reduceMotion = mediaQuery.matches;

    mediaQuery.addEventListener('change', (e) => {
      this.setReduceMotion(e.matches);
    });
  }

  setReduceMotion(reduce: boolean): void {
    this.reduceMotion = reduce;
    this.notifyListeners();
  }

  shouldReduceMotion(): boolean {
    return this.reduceMotion;
  }

  onChange(listener: (reduced: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.reduceMotion));
  }

  /**
   * Create a tween that respects reduced motion preference
   * When reduced motion is enabled, immediately applies the end state
   */
  createTween(scene: Phaser.Scene, config: AnimationConfig): Phaser.Tweens.Tween | null {
    if (this.reduceMotion) {
      // Immediately apply end state
      this.applyEndState(config);
      return null;
    }

    return scene.tweens.add(config as Phaser.Types.Tweens.TweenBuilderConfig);
  }

  /**
   * Create a chain of tweens that respects reduced motion preference
   * Returns the first tween in the chain, or null if reduced motion
   */
  createTweenChain(scene: Phaser.Scene, configs: AnimationConfig[]): Phaser.Tweens.Tween | null {
    if (this.reduceMotion) {
      // Apply all end states immediately
      configs.forEach((config) => this.applyEndState(config));
      return null;
    }

    if (configs.length === 0) return null;

    // Create tweens with onComplete chaining
    let firstTween: Phaser.Tweens.Tween | null = null;

    for (let i = configs.length - 1; i >= 0; i--) {
      const config = configs[i];
      const nextTween = i < configs.length - 1 ? configs[i + 1] : null;

      const tweenConfig: Phaser.Types.Tweens.TweenBuilderConfig = {
        ...(config as Phaser.Types.Tweens.TweenBuilderConfig),
        onComplete: nextTween ? () => {
          scene.tweens.add(nextTween as Phaser.Types.Tweens.TweenBuilderConfig);
        } : undefined,
      };

      if (i === 0) {
        firstTween = scene.tweens.add(tweenConfig);
      }
    }

    return firstTween;
  }

  private applyEndState(config: AnimationConfig): void {
    const targets = Array.isArray(config.targets) ? config.targets : [config.targets];

    targets.forEach((target: Record<string, unknown>) => {
      if (!target) return;

      // Apply numeric end values
      if (typeof config.alpha === 'number') target.alpha = config.alpha;
      if (typeof config.scale === 'number') {
        if ('setScale' in target && typeof target.setScale === 'function') {
          (target.setScale as (s: number) => void)(config.scale);
        } else {
          target.scale = config.scale;
        }
      }
      if (typeof config.x === 'number') target.x = config.x;
      if (typeof config.y === 'number') target.y = config.y;
      if (typeof config.rotation === 'number') target.rotation = config.rotation;
      if (typeof config.angle === 'number') target.angle = config.angle;

      // Handle scaleX/scaleY separately
      if (typeof config.scaleX === 'number') target.scaleX = config.scaleX;
      if (typeof config.scaleY === 'number') target.scaleY = config.scaleY;
    });
  }

  /**
   * Animate with a fallback for reduced motion
   * Useful for entrance animations
   */
  animateIn(
    scene: Phaser.Scene,
    target: Phaser.GameObjects.GameObject & { alpha?: number; y?: number; x?: number; scale?: number },
    config: {
      fromAlpha?: number;
      toAlpha?: number;
      fromY?: number;
      toY?: number;
      fromX?: number;
      toX?: number;
      fromScale?: number;
      toScale?: number;
      duration?: number;
      delay?: number;
      ease?: string;
    }
  ): void {
    const {
      fromAlpha = 0,
      toAlpha = 1,
      fromY,
      toY,
      fromX,
      toX,
      fromScale,
      toScale,
      duration = 300,
      delay = 0,
      ease = 'Power2',
    } = config;

    if (this.reduceMotion) {
      // Skip animation, apply end state
      target.alpha = toAlpha;
      if (toY !== undefined) target.y = toY;
      if (toX !== undefined) target.x = toX;
      if (toScale !== undefined && 'setScale' in target) {
        (target as { setScale: (s: number) => void }).setScale(toScale);
      }
      return;
    }

    // Set initial state
    target.alpha = fromAlpha;
    if (fromY !== undefined) target.y = fromY;
    if (fromX !== undefined) target.x = fromX;
    if (fromScale !== undefined && 'setScale' in target) {
      (target as { setScale: (s: number) => void }).setScale(fromScale);
    }

    // Animate to end state
    const tweenConfig: Phaser.Types.Tweens.TweenBuilderConfig = {
      targets: target,
      alpha: toAlpha,
      duration,
      delay,
      ease,
    };

    if (toY !== undefined) tweenConfig.y = toY;
    if (toX !== undefined) tweenConfig.x = toX;
    if (toScale !== undefined) tweenConfig.scale = toScale;

    scene.tweens.add(tweenConfig);
  }

  /**
   * Animate out with a callback
   */
  animateOut(
    scene: Phaser.Scene,
    target: Phaser.GameObjects.GameObject & { alpha?: number; y?: number; x?: number },
    config: {
      toAlpha?: number;
      toY?: number;
      toX?: number;
      duration?: number;
      ease?: string;
      onComplete?: () => void;
    }
  ): void {
    const { toAlpha = 0, toY, toX, duration = 200, ease = 'Power2', onComplete } = config;

    if (this.reduceMotion) {
      target.alpha = toAlpha;
      if (toY !== undefined) target.y = toY;
      if (toX !== undefined) target.x = toX;
      onComplete?.();
      return;
    }

    const tweenConfig: Phaser.Types.Tweens.TweenBuilderConfig = {
      targets: target,
      alpha: toAlpha,
      duration,
      ease,
      onComplete,
    };

    if (toY !== undefined) tweenConfig.y = toY;
    if (toX !== undefined) tweenConfig.x = toX;

    scene.tweens.add(tweenConfig);
  }

  /**
   * Pulse animation (e.g., for highlighting)
   * Returns null if reduced motion is enabled
   */
  pulse(
    scene: Phaser.Scene,
    target: Phaser.GameObjects.GameObject,
    config: {
      scale?: number;
      duration?: number;
      repeat?: number;
    } = {}
  ): Phaser.Tweens.Tween | null {
    if (this.reduceMotion) {
      return null;
    }

    const { scale = 1.1, duration = 500, repeat = -1 } = config;

    return scene.tweens.add({
      targets: target,
      scale: scale,
      duration,
      yoyo: true,
      repeat,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Floating animation (e.g., for particles, orbs)
   * Returns simplified or no animation when reduced
   */
  float(
    scene: Phaser.Scene,
    target: Phaser.GameObjects.GameObject & { y?: number },
    config: {
      amplitude?: number;
      duration?: number;
    } = {}
  ): Phaser.Tweens.Tween | null {
    if (this.reduceMotion) {
      return null;
    }

    const { amplitude = 10, duration = 2000 } = config;
    const startY = target.y || 0;

    return scene.tweens.add({
      targets: target,
      y: startY + amplitude,
      duration,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Get recommended animation duration
   * Returns 0 for reduced motion
   */
  getDuration(baseDuration: number): number {
    return this.reduceMotion ? 0 : baseDuration;
  }

  /**
   * Check if continuous animations should run
   * (like particle effects, ambient motion)
   */
  shouldAnimateContinuously(): boolean {
    return !this.reduceMotion;
  }

  /**
   * Get simplified particle config for reduced motion
   */
  getParticleConfig(fullConfig: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig): Phaser.Types.GameObjects.Particles.ParticleEmitterConfig {
    if (!this.reduceMotion) {
      return fullConfig;
    }

    // Extract numeric values from possibly complex config types
    const getNumericValue = (value: unknown, defaultVal: number): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'object' && value !== null && 'min' in value) {
        return (value as { min: number }).min;
      }
      return defaultVal;
    };

    const quantity = getNumericValue(fullConfig.quantity, 1);
    const lifespan = getNumericValue(fullConfig.lifespan, 1000);

    // Return simplified config with reduced particles and no movement
    return {
      ...fullConfig,
      quantity: Math.max(1, Math.floor(quantity / 4)),
      speedX: 0,
      speedY: 0,
      lifespan: Math.min(lifespan, 500),
      alpha: { start: 0.5, end: 0 },
    };
  }
}
